/**
 * Cart Item Card - Individual cart item display
 * Handles both guest_room and meeting_room types
 * Design: Clean card with hover states, lucide icons, generous spacing
 */
import { Calendar, Clock, Users, MapPin, Trash2, BedDouble, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/contexts/CartContext';
import { formatCartDate, formatCartPrice } from '../utils/cartFormatters';

export function CartItemCard({ item }) {
  const { removeFromCart } = useCart();
  const isGuestRoom = item.type === 'guest_room';
  const isMeetingRoom = item.type === 'meeting_room';

  const handleRemove = async () => {
    await removeFromCart(item.id);
  };

  return (
    <Card
      className="overflow-hidden transition-all duration-200 hover:shadow-md"
      data-testid={`cart-item-${item.id}`}
    >
      <CardContent className="p-4">
        <div className="flex gap-3">
          {/* Icon/Type Indicator */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              {isGuestRoom ? (
                <BedDouble className="w-6 h-6 text-primary" strokeWidth={1.5} />
              ) : (
                <Building2 className="w-6 h-6 text-primary" strokeWidth={1.5} />
              )}
            </div>
          </div>

          {/* Item Details */}
          <div className="flex-1 min-w-0 space-y-2">
            {/* Title & Type Badge */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <h4 className="font-semibold heading-font text-sm leading-tight">
                  {item.hotel_name}
                </h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isGuestRoom ? item.room_type : item.room_name}
                </p>
              </div>
              <Badge variant="outline" className="text-[10px] flex-shrink-0">
                {isGuestRoom ? 'Guest Room' : 'Meeting Room'}
              </Badge>
            </div>

            {/* Details Grid */}
            <div className="space-y-1.5 text-xs text-muted-foreground">
              {isGuestRoom && (
                <>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span className="mono">
                      {formatCartDate(item.check_in)} → {formatCartDate(item.check_out)}
                    </span>
                    <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0">
                      {item.nights}N
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span>{item.guests} {item.guests === 1 ? 'Guest' : 'Guests'}</span>
                  </div>
                </>
              )}

              {isMeetingRoom && (
                <>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span className="mono">{formatCartDate(item.booking_date)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span className="mono">
                      {item.start_time} - {item.end_time} ({item.duration_hours}h)
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span>{item.attendees} Attendees</span>
                  </div>
                </>
              )}
            </div>

            {/* Price & Remove Button */}
            <div className="flex items-center justify-between pt-2">
              <span className="font-bold mono text-base" data-testid={`cart-item-price-${item.id}`}>
                {formatCartPrice(item.base_price)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemove}
                className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                data-testid={`cart-item-remove-${item.id}`}
              >
                <Trash2 className="w-4 h-4 mr-1" strokeWidth={1.5} />
                Remove
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
